import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, MicOff, PhoneOff } from 'lucide-react';
type AgentState = 'idle' | 'listening' | 'thinking' | 'speaking';
const STATE_LABELS: Record<AgentState, string> = {
  idle: 'Tap to talk',
  listening: 'Listening',
  thinking: 'Thinking',
  speaking: 'Speaking'
};
function AuraMock({
  state
}: {
  state: AgentState;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    const size = 280;
    canvas.width = size * 2;
    canvas.height = size * 2;
    ctx.scale(2, 2);
    let frame = 0;
    let animId: number;
    const draw = () => {
      ctx.clearRect(0, 0, size, size);
      const cx = size / 2;
      const cy = size / 2;
      const t = frame * 0.012;
      const intensity = state === 'idle' ? 0.25 : state === 'listening' ? 0.5 : state === 'thinking' ? 0.7 : 0.9;
      const speed = state === 'idle' ? 0.4 : state === 'thinking' ? 1.6 : 0.9;
      const layers = state === 'idle' ? 3 : 4;
      for (let i = layers; i >= 0; i--) {
        const baseR = 40 + i * 18;
        const wobble = Math.sin(t * speed + i * 0.8) * (3 + intensity * 6);
        const r = baseR + wobble;
        const alpha = (0.06 + intensity * 0.05) * (1 - i * 0.12);
        const grad = ctx.createRadialGradient(cx, cy, r * 0.2, cx, cy, r);
        grad.addColorStop(0, `rgba(74, 154, 154, ${alpha * 1.4})`);
        grad.addColorStop(0.6, `rgba(74, 154, 154, ${alpha})`);
        grad.addColorStop(1, 'rgba(74, 154, 154, 0)');
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.fillStyle = grad;
        ctx.fill();
      }
      const coreAlpha = 0.12 + intensity * 0.2;
      const coreGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, 32);
      coreGrad.addColorStop(0, `rgba(74, 154, 154, ${coreAlpha})`);
      coreGrad.addColorStop(1, 'rgba(74, 154, 154, 0)');
      ctx.beginPath();
      ctx.arc(cx, cy, 32, 0, Math.PI * 2);
      ctx.fillStyle = coreGrad;
      ctx.fill();
      frame++;
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(animId);
  }, [state]);
  return <canvas ref={canvasRef} className="w-[280px] h-[280px]" data-id="element-2197" />;
}
export function VoiceView() {
  const [state, setState] = useState<AgentState>('idle');
  const [muted, setMuted] = useState(false);
  const handleTap = () => {
    if (state === 'idle') {
      setState('listening');
      setTimeout(() => setState('thinking'), 2500);
      setTimeout(() => setState('speaking'), 4500);
      setTimeout(() => setState('listening'), 7500);
    }
  };
  const handleEnd = () => setState('idle');
  return <div className="min-h-[calc(100vh-56px)] w-full flex flex-col items-center justify-center px-6" data-id="element-2198">
      <div className="flex flex-col items-center gap-10" data-id="element-2199">
        <div className="cursor-pointer" onClick={handleTap} data-id="element-2200">
          <AuraMock state={state} data-id="element-2201" />
        </div>
        <AnimatePresence mode="wait" data-id="element-2202">
          <motion.p key={state} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.6
        }} className="text-[#8a8680]/60 text-[14px] tracking-wide" data-id="element-2203">
            {STATE_LABELS[state]}
          </motion.p>
        </AnimatePresence>
        {state !== 'idle' && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.3
      }} className="flex items-center gap-4" data-id="element-2204">
            <button onClick={() => setMuted(!muted)} className="w-11 h-11 rounded-full flex items-center justify-center hover:bg-white/5 transition-colors" data-id="element-2205">
              {muted ? <MicOff size={18} className="text-[#8a8680]" data-id="element-2206" /> : <Mic size={18} className="text-[#8a8680]" data-id="element-2207" />}
            </button>
            <button onClick={handleEnd} className="w-11 h-11 rounded-full flex items-center justify-center hover:bg-white/5 transition-colors" data-id="element-2208">
              <PhoneOff size={18} className="text-[#8a8680]" data-id="element-2209" />
            </button>
          </motion.div>}
      </div>
    </div>;
}